import React from 'react';
import { Link } from 'react-router-dom';
import {
  PageHead, Section, Container, Eyebrow, SectionHead, Lede, Button, CardTitle, CardBody,
  Grid, Saying, ONE_LINER, CONTACT_MAILTO, SUBSTACK_URL,
} from '../components/ui';
import ProofBand from '../components/ProofBand';
import { useLatest } from '../components/Latest';
import { BOOK } from '../content/book';
import { ORG_JSON_LD } from '../content/proof';

/**
 * Home, from Srini's tree: the hero (with why we exist), then three pillars
 * with three children each. The argument, How we help, Thought leadership.
 * Each card is the link to its child.
 */

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const PillarCard = ({ who, head, body, cta, to, href, children }) => {
  const cls = 'group rounded-lg border border-navy-200 dark:border-navy-700 border-t-4 border-t-emerald-500 bg-white dark:bg-navy-850 p-5 md:p-[26px] flex flex-col hover:border-emerald-500 dark:hover:border-emerald-300 transition-colors';
  const inner = (
    <>
      {who && <div className="font-sans text-label uppercase font-semibold text-navy-500 dark:text-navy-400">{who}</div>}
      <CardTitle className="mt-2 group-hover:text-emerald-600 dark:group-hover:text-emerald-300">{head}</CardTitle>
      {body && <CardBody>{body}</CardBody>}
      {children}
      <span className="mt-auto pt-4 font-sans text-interface font-medium text-emerald-600 dark:text-emerald-300 inline-flex items-center gap-1">
        {cta}
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
      </span>
    </>
  );
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>
    : <Link to={to} className={cls}>{inner}</Link>;
};

const Pillar = ({ id, eyebrow, head, lede, to, more, tone, saying, tag, children }) => (
  <Section tone={tone} id={id}>
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div className="max-w-[92ch]">
        <Eyebrow>{eyebrow}</Eyebrow>
        <SectionHead>{head}</SectionHead>
        {lede && <p className="font-display text-lede mt-3 text-navy-600 dark:text-[#C9D2E0]">{lede}</p>}
      </div>
      <div className="hidden md:block"><Button to={to} variant="secondary">{more} <span aria-hidden="true">&rarr;</span></Button></div>
    </div>
    <Grid cols={3}>{children}</Grid>
    {saying && <Saying tag={tag} className="mt-12">{saying}</Saying>}
    <div className="mt-8 md:hidden"><Button to={to} variant="secondary">{more} <span aria-hidden="true">&rarr;</span></Button></div>
  </Section>
);

const HomePage = () => {
  const latest = useLatest(3);
  const essays = latest.filter((i) => i.kind === 'post');
  const newest = essays[0] || latest[0];
  return (
    <>
      <PageHead
        title={null}
        description={`Right Brain Labs is an AI capability incubator. We help humans and organizations build the cognitive infrastructure to think with AI, not follow it. Advisory, executive education at OSU Fisher, the Apprenticeship, the CoThink Compass, and the book Thinking with AI.`}
        path="/"
        image={BOOK.og}
        jsonLd={ORG_JSON_LD}
      />

      {/* Hero, light, with the why-we-exist line */}
      <section className="bg-navy-50 dark:bg-navy-900 pt-10 pb-10 md:pt-14 md:pb-12">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7 lg:col-span-7">
              <Eyebrow>Right Brain Labs</Eyebrow>
              <h1 className="font-display font-bold text-page-title tracking-tight text-navy-800 dark:text-white">
                The agency to think with AI, <span className="text-emerald-600 dark:text-emerald-300">not follow it.</span>
              </h1>
              <Lede>{ONE_LINER}</Lede>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button href={CONTACT_MAILTO}>Start a conversation</Button>
                <Button to="/compass" variant="secondary">Take the Compass</Button>
              </div>
              <p className="font-sans text-interface mt-6 text-navy-600 dark:text-[#C9D2E0]">
                Founded in New Albany, Ohio, on one conviction: as AI gets smarter, the humans running it have to get sharper.{' '}
                <Link to="/who-we-are" className="font-medium text-emerald-600 dark:text-emerald-300">Who we are</Link>
              </p>
            </div>
            <div className="md:col-span-5">
              <figure className="rounded-lg overflow-hidden border border-navy-200 dark:border-navy-700 bg-white dark:bg-navy-850">
                <picture>
                  <source type="image/webp" srcSet="/images/v2/hero-session-520.webp 520w, /images/v2/hero-session-775.webp 775w" sizes="(min-width: 700px) 40vw, 100vw" />
                  <img src="/images/v2/hero-session-775.jpg" srcSet="/images/v2/hero-session-520.jpg 520w, /images/v2/hero-session-775.jpg 775w" sizes="(min-width: 700px) 40vw, 100vw"
                    alt="Srini Koushik leading a Thinking with AI working session" width="775" height="969" className="w-full h-auto max-h-[300px] sm:max-h-[440px] md:max-h-[520px] object-cover object-[center_15%] sm:object-top" />
                </picture>
                <figcaption className="font-sans text-small text-navy-500 dark:text-navy-400 px-4 py-3 border-t border-navy-200 dark:border-navy-700">
                  A Thinking with AI working session
                </figcaption>
              </figure>
            </div>
          </div>
        </Container>
      </section>

      <ProofBand />

      {/* Pillar 1: The argument */}
      <Pillar id="why-it-matters" tone="card" eyebrow="Why it matters" to="/why-it-matters" more="The full argument"
        saying="It&rsquo;s not about learning to talk to dumb machines. It&rsquo;s about thinking with smart ones."
        head="The real danger with AI is not what it does. It is what it lets you not do.">
        <PillarCard head="Cognitive atrophy" cta="The Cognitive Rust Belt" to="/why-it-matters#cognitive-atrophy"
          body="Humans build mastery by thinking, doing, learning, and adapting. Hand that loop to AI and the capabilities you meant to augment atrophy, one unchallenged answer at a time." />
        <PillarCard head="AI Fluency" cta="The 5Cs" to="/why-it-matters#ai-fluency"
          body="Awareness and tool familiarity is AI Literacy. AI Fluency is a practiced capability built on five human strengths that gain value as AI gains capability." />
        <PillarCard head="The CoThink Compass" cta="Take the Compass, free" to="/compass"
          body="Ten questions, ten minutes. It places you on the AI Fluency Ladder and sends a report with next steps to your inbox." />
      </Pillar>

      {/* Pillar 2: How we help */}
      <Pillar id="what-we-do" tone="page" eyebrow="What we do" to="/what-we-do" more="How we work"
        saying="What you no longer practice, you no longer possess." tag="Cognitive Agility"
        head="Three ways in. One outcome: a practice your people run without us.">
        <PillarCard who="Leaders, boards, organizations" head="AI Transformation Advisory" cta="How advisory works" to="/what-we-do#advisory"
          body="Strategy, governance, and capability for the decisions AI forces, built on 35 years of running technology organizations." />
        <PillarCard who="Executive teams" head="Executive Education at OSU Fisher" cta="About the program" to="/what-we-do#osu-fisher"
          body="Intensives through the Fisher College of Business at The Ohio State University, with Ohio State credentialing." />
        <PillarCard who="Leadership teams, 10 to 15 people" head="The Apprenticeship" cta="About the Apprenticeship" to="/what-we-do#apprenticeship"
          body="A cohort takes on a real problem under practitioner coaching for 30 to 60 days. The Apprenticeship ends. The capability stays." />
      </Pillar>

      {/* Pillar 3: Thought leadership */}
      <Pillar id="what-we-think" tone="card" eyebrow="What we think" to="/what-we-think" more="The book, the essays, the talks"
        saying="The machine keeps getting smarter. Our job is to make sure the human does too."
        head="The argument, in the open.">
        <PillarCard who="The book, out now" head={`${BOOK.title}: ${BOOK.subtitle}`} cta="Hardcover, paperback, Kindle" to="/book">
          <div className="mt-4 flex gap-4 items-start">
            <img src={BOOK.coverSmall} alt="" width="360" height="566" loading="lazy" className="w-[88px] h-auto rounded-sm border border-navy-200 dark:border-navy-700" />
            <p className="font-sans text-interface text-navy-600 dark:text-[#C9D2E0]">{BOOK.descriptionLine} By {BOOK.author}, {BOOK.publisher}, {BOOK.publishedText}.</p>
          </div>
        </PillarCard>
        <PillarCard who="Substack" head="Essays, most weeks" cta="Read the Substack" href={SUBSTACK_URL}>
          {newest ? (
            <div className="mt-3">
              <div className="font-sans text-[17px] font-medium text-navy-800 dark:text-white">{newest.title}</div>
              <div className="font-mono text-meta mt-1 text-navy-500 dark:text-navy-400">{formatDate(newest.date)}</div>
              {newest.summary && <p className="font-sans text-interface mt-2 text-navy-600 dark:text-[#C9D2E0]">{newest.summary}</p>}
            </div>
          ) : (
            <CardBody>Short essays on judgment, capability, and what leaders give up when they let the machine do the thinking.</CardBody>
          )}
        </PillarCard>
        <PillarCard who="Talks and articles" head="One argument, many rooms" cta="Talks, papers, and articles" to="/what-we-think#articles"
          body="Keynotes for executive teams and conferences, board sessions, working days with intact teams, and the articles behind them." />
      </Pillar>
    </>
  );
};

export default HomePage;
